<template>
  <div class="px-6 py-6 space-y-6 container">
    <div class="space-y-6 w-150">
      <TextInput
        v-model="apiKey"
        label="OpenAI key"
        placeholder=""
        :disabled="!isAdmin"
        @keydown.enter="configureStudiesQA(apiKey)"
      >
        <template #label="{ id }">
          <div class="flex items-center gap-2">
            <label
              class="text-sm text-primary font-medium pointer-events-none"
              :for="id"
            >
              Enter you OpenAI key
            </label>
          </div>
        </template>
        <template #suffix>
          <button
            v-if="isAdmin"
            class="text-white px-2 py-1 bg-primary-500 rounded-md text-sm hover:brightness-110"
            @click="configureStudiesQA(apiKey)"
          >
            Save
          </button>
        </template>
      </TextInput>
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <h3 class="text-sm text-primary font-medium">
            AI Model Configuration
          </h3>
          <a
            href="https://openai.com/api/pricing/"
            target="_blank"
            rel="noopener noreferrer"
            class="text-xs text-primary-500 hover:text-primary-600 underline"
          >
            View pricing
          </a>
        </div>
        <div class="space-y-3">
          <ModelSelector
            v-model="models.dataExtractionModel"
            label="Data Extraction Model"
            :disabled="!isAdmin"
            show-cost-warning
            @update:model-value="
              (value: string) => selectModel('dataExtractionModel', value)
            "
          />
          <ModelSelector
            v-model="models.synthesisModel"
            label="Synthesis Model"
            :disabled="!isAdmin"
            show-cost-warning
            @update:model-value="
              (value: string) => selectModel('synthesisModel', value)
            "
          />
          <ModelSelector
            v-model="models.screeningModel"
            label="Screening Model"
            :disabled="!isAdmin"
            show-cost-warning
            @update:model-value="
              (value: string) => selectModel('screeningModel', value)
            "
          />
          <ModelSelector
            v-model="models.medicalBackgroundPresetModel"
            label="Medical Background Preset Model"
            :disabled="!isAdmin"
            @update:model-value="
              (value: string) =>
                selectModel('medicalBackgroundPresetModel', value)
            "
          />
          <ModelSelector
            v-model="models.notifiedBodyPresetModel"
            label="Notified Body Preset Model"
            :disabled="!isAdmin"
            @update:model-value="
              (value: string) => selectModel('notifiedBodyPresetModel', value)
            "
          />
        </div>
      </div>
    </div>
    <div v-if="isCuttingEdge" class="space-y-4">
      <div class="flex items-center justify-between">
        <h3 class="text-sm text-primary font-medium">Data Extraction Prompt</h3>
      </div>
      <div class="space-y-3">
        <textarea
          v-model="promptModel"
          :disabled="!isAdmin"
          placeholder="Leave empty to use default prompt"
          class="w-full min-h-60 p-3 text-sm font-mono rounded-md border border-slate-300 focus:outline-hidden focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:bg-black/5 disabled:cursor-not-allowed resize-y"
        />
        <div v-if="isAdmin" class="flex gap-2">
          <button
            class="text-white px-4 py-2 bg-primary-500 rounded-md text-sm hover:brightness-110"
            @click="saveDataExtractionPrompt"
          >
            Save Prompt
          </button>
          <button
            class="text-primary-500 px-4 py-2 bg-white border border-primary-500 rounded-md text-sm hover:bg-primary-50"
            @click="resetPromptToDefault"
          >
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ModelSelector from '@app/components/Settings/ModelSelector.vue'
import TextInput from '@app/components/Global/Inputs/TextInput.vue'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import useAiAssistant from '@app/composables/useAiAssistant'
import { AuthKey, DevToolboxKey } from '@app/injectionKeys'
import { injectStrict } from '@app/utils/injectStrict'
import { Role } from '@core/domain/types/role.type'
import { computed, onMounted, ref } from 'vue'

const {
  apiKey,
  aiModels,
  dataExtractionPrompt,
  configure,
  updateModels,
  updateDataExtractionPrompt,
  resetDataExtractionPrompt,
  initModels,
  initDataExtractionPrompt,
} = useAiAssistant()
const loading = useLoading()
const snackbar = useSnackbar()
const { user } = injectStrict(AuthKey)
const isAdmin = computed(() => {
  return user.value?.role === Role.ADMIN
})

const { isCuttingEdge } = injectStrict(DevToolboxKey)
const models = ref({
  dataExtractionModel: 'gpt-4.1-mini',
  synthesisModel: 'gpt-4.1-mini',
  screeningModel: 'gpt-4.1-mini',
  medicalBackgroundPresetModel: 'gpt-4.1',
  notifiedBodyPresetModel: 'gpt-4.1',
})

const promptModel = ref<string>('')

onMounted(async () => {
  await initModels()
  models.value = { ...aiModels.value }
  await initDataExtractionPrompt()
  promptModel.value = dataExtractionPrompt.value || ''
})

async function configureStudiesQA(apiKey: string) {
  loading.start()
  try {
    await configure(apiKey)
    snackbar.success('Success')
  } catch (_) {
    snackbar.error(
      'Error while configuring StudiesQA key. Please check your key and try again.',
    )
  } finally {
    loading.stop()
  }
}

async function selectModel(key: keyof typeof models.value, value: string) {
  if (!isAdmin.value) return

  models.value[key] = value

  loading.start()
  try {
    await updateModels({ [key]: value })
    snackbar.success('Success')
  } catch (_) {
    snackbar.error('Error while updating AI model')
  } finally {
    loading.stop()
  }
}

async function saveDataExtractionPrompt() {
  if (!isAdmin.value) return

  loading.start()
  try {
    await updateDataExtractionPrompt(promptModel.value)
    snackbar.success('Success')
  } catch (_) {
    snackbar.error('Error while updating data extraction prompt')
  } finally {
    loading.stop()
  }
}

async function resetPromptToDefault() {
  if (!isAdmin.value) return

  loading.start()
  try {
    const defaultPrompt = await resetDataExtractionPrompt()
    promptModel.value = defaultPrompt
    await updateDataExtractionPrompt(defaultPrompt)
    snackbar.success('Success')
  } catch (_) {
    snackbar.error('Error while resetting prompt')
  } finally {
    loading.stop()
  }
}
</script>
